/**
 * aC Men's Hub の Channel Talk チャット状態の共有定義。
 * 表示定義（infra/generator/ac-mens-hub-daily の cs__chats）・API（apps/api/ac-mens-hub の
 * cs-inquiry）・Web（apps/web/ac-mens-hub のチャット一覧）が同じ値を指す。
 *
 * 状態は2種類ある。Channel Talk が返す実在の状態6つと、Hub の表示定義が取り込みの問題を
 * 表すために入れる2つ（未取得・不明）。画面では後者を実在の状態と分けて見せる。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Channel Talk のチャット状態（実在の6状態と取り込み状況の2値）を、BigQuery の表示定義・API の絞り込み・画面のラベルで共有する
 * @graph-connects none
 */
/**
 * チャットのメタ（状態・担当者など）をまだ取り込めていないことを表す状態値。
 * cs__chats がメッセージはあるのに、共用の写しにもチャット台帳にも行が無いチャットに入れる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business メッセージはあるのにチャットのメタ（状態・担当者など）をまだ取り込めていないことを表す状態値
 * @graph-connects none
 */
export const CS_CHAT_STATE_UNFETCHED = 'unfetched';
/**
 * チャットのメタは取り込めたが、状態が実在の6状態以外（空・廃止された値など）だったことを表す状態値。
 * cs__chats がこの値に寄せるので、画面の「不明」と絞り込み state=unknown は同じ集合になる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business チャットのメタは取り込めたが状態が実在の6状態以外（空・廃止された値など）だったことを表す状態値
 * @graph-connects none
 */
export const CS_CHAT_STATE_UNKNOWN = 'unknown';
/**
 * Channel Talk が返す実在のチャット状態（画面に出す順）。
 *
 * `initial` は、ALF だけ、またはオペレーターに振り分けられていない会話（ALF は Channel Talk の AI エージェント）を指す
 * （顧客は送信済み。画面では「オペレーター未接続」）。解決数は `closed` だけを数えるので、`initial` のまま終わった
 * 会話は解決数に含まれない。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Channel Talk が返す実在の6状態を画面に出す順で並べる
 * @graph-connects none
 */
export const CS_CHAT_STATES = [
    'opened',
    'queued',
    'snoozed',
    'initial',
    'missed',
    'closed',
];
/**
 * AI に会話を渡すときに書く、実在の6状態の日本語の説明。英語の状態値をそのまま渡すと AI が意味を推測するので、
 * 業務での意味を日本語で渡す。`initial` は ALF だけ、またはオペレーターに振り分けられていない会話であることを明示する。
 * 画面の表示ラベルは i18n（ja/en/vi）側にあり、これは AI 向けの説明文。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 問い合わせ AI 分析のプロンプトに書く、Channel Talk の実在の6状態の日本語の説明
 * @graph-connects none
 */
export const CS_CHAT_STATE_PROMPT_LABELS = {
    opened: '対応中',
    queued: '順番待ち',
    snoozed: '保留中',
    initial: 'オペレーター未接続（ALF のみ、または振り分け待ち）',
    missed: '対応漏れ',
    closed: '解決済',
};
/**
 * 取り込み状況を表す状態値（画面では実在の状態の後ろに、見出しで分けて出す）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 取り込み状況を表す2つの状態値（実在の状態の後ろに出す）
 * @graph-connects none
 */
export const CS_CHAT_IMPORT_STATES = [CS_CHAT_STATE_UNFETCHED, CS_CHAT_STATE_UNKNOWN];
/**
 * チャット一覧の状態で絞り込める値の全集合。API の入力検証と画面の選択肢が同じ集合を指す。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business チャット一覧を状態で絞れる値の全集合。API の入力検証と画面の選択肢が共有する
 * @graph-connects none
 */
export const CS_CHAT_FILTER_STATES = [...CS_CHAT_STATES, ...CS_CHAT_IMPORT_STATES];
/**
 * 値が Channel Talk の実在の6状態のどれかかを判定する。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 状態値が Channel Talk の実在の6状態のどれかかを判定する
 * @graph-connects none
 */
export function isCsChatState(state) {
    return CS_CHAT_STATES.some((s) => s === state);
}
/**
 * 値がチャット一覧で絞れる状態（実在の6状態＋未取得・不明）のどれかかを判定する。
 * オブジェクトの組み込みキー（toString など）は含めない。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 状態値が実在の6状態または未取得・不明のどれかかを判定する
 * @graph-connects none
 */
export function isCsChatFilterState(state) {
    return CS_CHAT_FILTER_STATES.some((s) => s === state);
}
//# sourceMappingURL=cs-chat-state.js.map